/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-continue */
import React from 'react';
import { Tooltip, Popover } from 'antd';
import { FormattedMessage } from 'react-intl';
import { EllipsedText } from '../TextUtils';
import PersonalEmailIcon from '../../Icons/EmailTypeIcons/PersonalEmailIcon';
import ProfessionalEmailIcon from '../../Icons/EmailTypeIcons/ProfessionalEmailIcon';
import styles from './EmailUtils.module.scss';
import { validateStatus } from '../ContactUtils';
import messages from '../../Containers/ManualSearchV2/ManualSearchMessages';

const getSortedEmailsArray = array => {
  return array.sort((a, b) => a.EmailAddress.localeCompare(b.EmailAddress));
};

const mapEmailTypeToIcon = ({ emailType }) => {
  if (!emailType) {
    return null;
  }
  const lowerCasedEmailType = emailType?.toLowerCase();
  if (lowerCasedEmailType === 'personal') {
    return (
      <Tooltip title={<FormattedMessage {...messages.personalEmailLabel} />}>
        <PersonalEmailIcon />
      </Tooltip>
    );
  }
  if (lowerCasedEmailType === 'professional') {
    return (
      <Tooltip title={<FormattedMessage {...messages.workEmailLabel} />}>
        <ProfessionalEmailIcon />
      </Tooltip>
    );
  }
  return null;
};

const getSortedEmailsByEmailTypes = ({ emails }) => {
  if (!emails?.length) {
    return emails;
  }
  const invalidSpamEmails = [];
  const validEmails = [];
  const userAddedEmails = [];
  const personalEmails = [];
  const workEmails = [];
  for (let i = 0; i < emails.length; i += 1) {
    const email = emails[i];
    const isEmailInvalid = validateStatus(email.ValidityStatus);
    const isEmailInvalidOrRefunded = isEmailInvalid || email.IsRefunded;
    if (isEmailInvalidOrRefunded) {
      invalidSpamEmails.push(email);
      continue;
    }
    if (email.IsAddedByUser) {
      userAddedEmails.push(email);
      continue;
    }
    if (!email.Type) {
      validEmails.push(email);
      continue;
    }
    if (email.Type === 'Personal') {
      personalEmails.push(email);
    } else workEmails.push(email);
  }
  let sortedEmails = [];
  if (userAddedEmails.length) sortedEmails = [...getSortedEmailsArray(userAddedEmails)];
  if (validEmails.length) sortedEmails = [...sortedEmails, ...getSortedEmailsArray(validEmails)];
  if (personalEmails.length) sortedEmails = [...sortedEmails, ...getSortedEmailsArray(personalEmails)];
  if (workEmails.length) sortedEmails = [...sortedEmails, ...getSortedEmailsArray(workEmails)];
  if (invalidSpamEmails) sortedEmails = [...sortedEmails, ...getSortedEmailsArray(invalidSpamEmails)];
  return sortedEmails;
};

const getEmailWithPopover = ({ popoverContent, email, onVisibilityChange, isReportInvalidEmailButtonClicked }) => {
  const { EmailAddress: emailAddress } = email;
  return !email.IsRefunded && popoverContent ? (
    <Popover
      mouseEnterDelay={0.2}
      placement="top"
      overlayStyle={{ paddingBottom: '0px' }}
      content={popoverContent}
      onVisibleChange={onVisibilityChange}
      trigger={isReportInvalidEmailButtonClicked ? 'click' : 'hover'}
      getPopupContainer={trigger => trigger.parentNode}
    >
      {emailAddress}
    </Popover>
  ) : (
    emailAddress
  );
};

const getEmailAddressWithEmailTypeIcon = ({
  email,
  maxEmailLength,
  isEllipsedTextForManualEmails,
  popoverContent,
  onVisibilityChange,
  isReportInvalidEmailButtonClicked,
}) => {
  const { Type: emailType, EmailAddress: emailAddress } = email;
  return (
    <span>
      {(emailType || isEllipsedTextForManualEmails) && maxEmailLength ? (
        <EllipsedText text={emailAddress} maxTextLength={maxEmailLength} />
      ) : (
        getEmailWithPopover({ popoverContent, email, onVisibilityChange, isReportInvalidEmailButtonClicked })
      )}
      {emailType ? <span className={styles.emailTypeIcon}>{mapEmailTypeToIcon({ emailType })}</span> : null}
    </span>
  );
};

export const getPreHeaderValidationStatus = preHeaderRef => {
  const preHeaderText = preHeaderRef?.current?.quillRef.getText().trim();
  if (preHeaderText) {
    return preHeaderText.length > 100 ? 'error' : '';
  }
  return '';
};

export const getEmailValidationStatus = Contact => {
  const { Emails } = Contact;
  if (Emails && Emails.length > 0) {
    const inValidEmails = Emails.filter(
      email => !email.IsRefunded && (email.ValidityStatus === 'Spam' || email.ValidityStatus === 'Invalid')
    );
    const notRefundedAndValidEmails = Emails.filter(
      email =>
        !email.IsRefunded &&
        !email.IsUnsubscribeByUser &&
        (email.EmailSubscriptionStatus === 'Subscribed' ||
          email.ValidityStatus === 'Valid' ||
          email.ValidityStatus === 'Unknown')
    );
    return !(
      (notRefundedAndValidEmails.length === 0 && inValidEmails.length > 0) ||
      notRefundedAndValidEmails.length === 0
    );
  }
  return false;
};

export function disableTrackingPixelsForPreview(html) {

  const container = document.createElement('div');
  container.innerHTML = html;
  // Remove tracking pixels
  const images = container.querySelectorAll('img');

  images.forEach(img => {
    const width = img.width || parseInt(img.getAttribute('width'), 10) || 0;
    const height = img.height || parseInt(img.getAttribute('height'), 10) || 0;
    const style = img.getAttribute('style') || '';
    const parentStyle = img.parentElement?.getAttribute('style') || '';
    const isTiny = (width <= 1 && height <= 1);
    const isHidden = /display\s*:\s*none|opacity\s*:\s*0|visibility\s*:\s*hidden/i.test(style + parentStyle);
    const isZeroSize = width === 0 || height === 0;

    if (isTiny || isHidden || isZeroSize) {
      img.removeAttribute('src');
      img.style.display = 'none';
    }
  });

  // Disable all links but keep them visible
  const links = container.querySelectorAll('a');

  links.forEach(link => {
    link.addEventListener('click', e => e.preventDefault());
    link.style.cursor = 'not-allowed';
    link.style.pointerEvents = 'none'; // ensures click is blocked
  });

  return container.innerHTML;
}


export { mapEmailTypeToIcon, getSortedEmailsByEmailTypes, getEmailAddressWithEmailTypeIcon };
